import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  isBrowser, 
  hasIDB, 
  openIDB, 
  safeIDBTransaction, 
  keepAlive, 
  safeAddEventListener,
  safeLocalStorage 
} from '../utils/browserGuards';
import { featureManager, autoDisableOnError } from '../utils/featureFlags';

/**
 * Auto-save hook for form data with debouncing, periodic saves, and session protection
 * @param {Object} options - Configuration options
 * @param {boolean} options.isDirty - Whether form has unsaved changes
 * @param {Object} options.data - Current form data to save
 * @param {Function} options.saveFn - Function to save draft (should return Promise)
 * @param {Function} options.onSaveSuccess - Callback after successful save (optional)
 * @param {number} options.debounceMs - Debounce delay in milliseconds (default: 1500)
 * @param {number} options.intervalMs - Periodic save interval in milliseconds (default: 30000)
 * @param {string} options.formId - Unique identifier for this form (e.g., 'tax-certificate')
 * @param {string} options.orderId - Order ID for local storage key
 */
export const useAutoSave = ({
  isDirty,
  data,
  saveFn,
  onSaveSuccess,
  debounceMs = 1500,
  intervalMs = 30000,
  formId,
  orderId
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [error, setError] = useState(null);
  // Jun 2, 2026 — Lock-aware autosave.
  // When the backend returns 423 (e.g. order is locked by QC review or
  // completion), we want to:
  //   • stop the periodic-save heartbeat so we don't spam the server
  //     with retries that will all fail (the prod report showed 8
  //     consecutive 423s in the network tab)
  //   • surface a sticky, user-readable reason instead of "Save failed:
  //     Request failed with status code 423"
  //   • automatically clear the pause when the user makes new edits, so
  //     if the lock was released by an admin/QC the next change retries.
  const [lockedError, setLockedError] = useState(null);
  const lockedErrorRef = useRef(null);
  
  const debounceTimeoutRef = useRef(null);
  const intervalRef = useRef(null);
  const lastSaveTimeRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  
  // IndexedDB setup for local backup with safety checks
  const openDB = useCallback(async () => {
    if (!featureManager.isEnabled('FEATURE_INDEXEDDB_BACKUP')) {
      return null;
    }
    
    if (!hasIDB) {
      autoDisableOnError('FEATURE_INDEXEDDB_BACKUP', 'IndexedDB not available');
      return null;
    }
    
    try {
      return await openIDB('TitleFlowDrafts', 1, (db, event) => {
        if (!db.objectStoreNames.contains('drafts')) {
          const store = db.createObjectStore('drafts', { keyPath: 'id' });
          store.createIndex('orderId', 'orderId', { unique: false });
          store.createIndex('formId', 'formId', { unique: false });
          store.createIndex('timestamp', 'timestamp', { unique: false });
        }
      });
    } catch (error) {
      console.error('Failed to open IndexedDB:', error);
      autoDisableOnError('FEATURE_INDEXEDDB_BACKUP', error, 'openDB');
      return null;
    }
  }, []);
  
  // Save to local backup with error handling
  const saveLocalBackup = useCallback(async (data) => {
    if (!featureManager.isEnabled('FEATURE_INDEXEDDB_BACKUP')) {
      // Fallback to localStorage if available
      if (safeLocalStorage.set(`draft_${orderId}_${formId}`, JSON.stringify({
        data,
        timestamp: new Date().toISOString()
      }))) {
        console.log('Local backup saved to localStorage:', `draft_${orderId}_${formId}`);
      }
      return;
    }
    
    try {
      const db = await openDB();
      if (!db) {
        // Fallback to localStorage
        safeLocalStorage.set(`draft_${orderId}_${formId}`, JSON.stringify({
          data,
          timestamp: new Date().toISOString()
        }));
        return;
      }

      const backup = {
        id: `${orderId}_${formId}`,
        orderId,
        formId,
        data,
        timestamp: new Date().toISOString(),
        version: Date.now()
      };
      
      const result = await safeIDBTransaction(db, 'drafts', 'readwrite', (store) => {
        return store.put(backup);
      });
      
      if (result !== null) {
        console.log('Local backup saved to IndexedDB:', backup.id);
      } else {
        // Fallback to localStorage
        safeLocalStorage.set(`draft_${orderId}_${formId}`, JSON.stringify({
          data,
          timestamp: new Date().toISOString()
        }));
      }
    } catch (error) {
      console.error('Failed to save local backup:', error);
      autoDisableOnError('FEATURE_INDEXEDDB_BACKUP', error, 'saveLocalBackup');
      
      // Fallback to localStorage
      safeLocalStorage.set(`draft_${orderId}_${formId}`, JSON.stringify({
        data,
        timestamp: new Date().toISOString()
      }));
    }
  }, [orderId, formId, openDB]);
  
  // Get local backup with fallbacks
  const getLocalBackup = useCallback(async () => {
    // Try IndexedDB first
    if (featureManager.isEnabled('FEATURE_INDEXEDDB_BACKUP')) {
      try {
        const db = await openDB();
        if (db) {
          const result = await safeIDBTransaction(db, 'drafts', 'readonly', (store) => {
            return store.get(`${orderId}_${formId}`);
          });
          
          if (result) {
            return result;
          }
        }
      } catch (error) {
        console.error('Failed to get backup from IndexedDB:', error);
      }
    }
    
    // Fallback to localStorage
    try {
      const stored = safeLocalStorage.get(`draft_${orderId}_${formId}`);
      if (stored) {
        const parsed = JSON.parse(stored);
        return {
          id: `${orderId}_${formId}`,
          orderId,
          formId,
          data: parsed.data,
          timestamp: parsed.timestamp,
          version: new Date(parsed.timestamp).getTime()
        };
      }
    } catch (error) {
      console.error('Failed to parse localStorage backup:', error);
    }
    
    return null;
  }, [orderId, formId, openDB]);
  
  // Clear local backup with fallbacks
  const clearLocalBackup = useCallback(async () => {
    // Clear from IndexedDB
    if (featureManager.isEnabled('FEATURE_INDEXEDDB_BACKUP')) {
      try {
        const db = await openDB();
        if (db) {
          await safeIDBTransaction(db, 'drafts', 'readwrite', (store) => {
            return store.delete(`${orderId}_${formId}`);
          });
          console.log('Local backup cleared from IndexedDB:', `${orderId}_${formId}`);
        }
      } catch (error) {
        console.error('Failed to clear IndexedDB backup:', error);
      }
    }
    
    // Clear from localStorage
    safeLocalStorage.remove(`draft_${orderId}_${formId}`);
  }, [orderId, formId, openDB]);
  
  // Perform save with retry logic and error handling
  const performSave = useCallback(async (data, isEmergency = false) => {
    if (!data || isSaving || !featureManager.isEnabled('FEATURE_AUTO_SAVE')) return false;
    // Jun 2, 2026 — Skip while we know the order is locked. The user's
    // next genuine edit (which updates `data` → resets the lock via the
    // dirty-effect below) will retry once; if it succeeds the pause
    // clears, if it 423s again it just re-arms.
    if (lockedErrorRef.current && !isEmergency) {
      return false;
    }
    
    setIsSaving(true);
    setError(null);
    
    try {
      // Save to local backup first (if enabled)
      await saveLocalBackup(data);
      
      // Attempt server save
      const result = await saveFn(data);
      
      // Update last saved time
      const now = new Date();
      setLastSaved(now);
      lastSaveTimeRef.current = now;
      // Successful save → clear any previous lock pause so the periodic
      // heartbeat can resume.
      if (lockedErrorRef.current) {
        lockedErrorRef.current = null;
        setLockedError(null);
      }
      
      // Clear local backup on successful server save
      if (!isEmergency) {
        await clearLocalBackup();
      }
      
      console.log('Auto-save successful:', {
        formId,
        orderId,
        timestamp: now.toISOString(),
        isEmergency
      });
      
      // Call success callback if provided
      if (onSaveSuccess && typeof onSaveSuccess === 'function') {
        onSaveSuccess();
      }
      
      return true;
    } catch (error) {
      // Jun 2, 2026 — Lock-aware error handling.
      // The save fn flags 423 with isLockedError so we know to pause the
      // retry loop AND surface the human-readable reason (consumer can
      // render it via the new `lockedError` return value).
      if (error && error.isLockedError === true) {
        const lockInfo = {
          message: error.message || 'Order is locked for editing.',
          orderStatus: error.lockedOrderStatus || null,
          requiresOverrideReason: !!error.requiresOverrideReason,
          at: new Date().toISOString(),
        };
        lockedErrorRef.current = lockInfo;
        setLockedError(lockInfo);
        setError(lockInfo.message);
        return false;
      }
      // Check if this is a silent validation error
      if (error && error.silent === true) {
        // Don't set error state for validation errors - they're handled by inline validation
        console.log('Auto-save blocked by validation:', error.message);
        return false;
      }
      
      console.error('Auto-save failed:', error);
      setError(error.message || 'Save failed');
      
      // Keep local backup if server save fails
      console.log('Draft saved locally due to server error');
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [saveFn, formId, orderId, isSaving, saveLocalBackup, clearLocalBackup, onSaveSuccess]);
  
  // Debounced save
  const debouncedSave = useCallback((data) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    debounceTimeoutRef.current = setTimeout(() => {
      if (isDirty && data) {
        performSave(data);
      }
    }, debounceMs);
  }, [isDirty, data, debounceMs, performSave]);
  
  // Emergency save (for page unload, etc.)
  const emergencySave = useCallback(async (data) => {
    if (isDirty && data) {
      console.log('Emergency save triggered');
      await performSave(data, true);
    }
  }, [isDirty, performSave]);
  
  // Auto-save on data change (debounced)
  // Jun 2, 2026 — A user edit AFTER a lock-failure should retry exactly
  // once. We clear lockedErrorRef so debouncedSave / performSave runs,
  // and let the next 423 (if the lock is still active) re-arm the pause.
  // This gives the user immediate feedback when the lock has been
  // released without nailing the server in a tight retry loop.
  useEffect(() => {
    if (isDirty && data) {
      if (lockedErrorRef.current) {
        lockedErrorRef.current = null;
        setLockedError(null);
      }
      debouncedSave(data);
    }
    
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [data, isDirty, debouncedSave]);
  
  // Periodic heartbeat save
  useEffect(() => {
    if (isDirty) {
      intervalRef.current = setInterval(() => {
        const now = new Date();
        const timeSinceLastSave = lastSaveTimeRef.current 
          ? now - lastSaveTimeRef.current 
          : intervalMs + 1000;
        
        if (timeSinceLastSave > intervalMs && data) {
          console.log('Periodic heartbeat save triggered');
          performSave(data);
        }
      }, intervalMs);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isDirty, data, intervalMs, performSave]);
  
  // Hard save triggers with browser API guards
  useEffect(() => {
    if (!isBrowser) return;
    
    const handleBeforeUnload = (event) => {
      if (isDirty && data && featureManager.isEnabled('FEATURE_AUTO_SAVE')) {
        // Send keepalive beacon with safe fallback
        keepAlive('/api/keepalive', { orderId, formId, timestamp: new Date().toISOString() });
        
        // Emergency save
        emergencySave(data);
        
        // Standard beforeunload message
        event.preventDefault();
        event.returnValue = '';
        return '';
      }
    };
    
    const handleVisibilityChange = () => {
      if (document.hidden && isDirty && data && featureManager.isEnabled('FEATURE_AUTO_SAVE')) {
        console.log('Tab hidden - emergency save');
        emergencySave(data);
      }
    };
    
    // Use safe event listeners
    const cleanupBeforeUnload = safeAddEventListener(window, 'beforeunload', handleBeforeUnload);
    const cleanupVisibilityChange = safeAddEventListener(document, 'visibilitychange', handleVisibilityChange);
    
    return () => {
      cleanupBeforeUnload();
      cleanupVisibilityChange();
    };
  }, [isDirty, data, emergencySave, orderId, formId]);
  
  // Save on route change
  useEffect(() => {
    return () => {
      if (isDirty && data) {
        console.log('Route change - emergency save');
        emergencySave(data);
      }
    };
  }, [location.pathname]);
  
  // Manual save function
  const manualSave = useCallback(async () => {
    if (data) {
      return await performSave(data);
    }
    return false;
  }, [data, performSave]);
  
  // Format last saved time
  const formatLastSaved = useCallback((timestamp) => {
    if (!timestamp) return null;
    
    // eslint-disable-next-line no-restricted-syntax -- DATE-DISPLAY-001: intentional browser-local autosave clock
    return timestamp.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  }, []);
  
  return {
    isSaving,
    lastSaved,
    lastSavedFormatted: formatLastSaved(lastSaved),
    error,
    lockedError,
    manualSave,
    getLocalBackup,
    clearLocalBackup
  };
};